import { useEffect, useMemo, useState } from 'react'
import { sum } from 'lodash'
import { useModals } from 'almanac-ui/Modal'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { Schedule, useAddToBrandPlanSchedule } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { SelectBrandPlanSchedule } from './SelectBrandPlanSchedule'
import { ReviewPostTimes } from './ReviewPostTimes'
import { AddMoreContent } from './AddMoreContent'
import { ConfirmationModal } from '../components/ConfirmationModal'
import { BrandPlan, BrandPlanSchedule } from 'types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { MinimalMediaFile } from '../../Types'
import { Additionals, AddMoreContentModalProps, AddMoreContentModalStep } from './Types'
import { Modal } from 'almanac-ui/Modal'
import { useBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import dayjs from 'dayjs'
import { collectResults } from 'utils/query'
import { TopicAndMedia } from './TopicAndMedia'
import { generateSchedule } from './utils'
import { CreateSelect } from './CreateSelect'

const INITIAL_COUNT_STATE: Additionals = {
  instagram_post: 0,
  instagram_reel: 0,
  instagram_story: 0,
  tiktok_post: 0,
  youtube_short: 0,
  facebook_post: 0,
  linkedin_post: 0,
  twitter_post: 0,
  twitter_thread: 0,
  newsletter: 0,
  blog_post: 0,
  crosspost_post: 0,
  video_crosspost_post: 0,
}

export const AddMoreContentModalWrapper = (props: AddMoreContentModalProps) => {
  const { data: brandPlan } = useCurrentBrandPlan()

  if (!brandPlan) return null

  return <AddMoreContentModal {...props} brandPlan={brandPlan} />
}

const AddMoreContentModal = ({
  initialTopic,
  initialMediaFiles,
  onSuccess,
  date,
  brandPlan,
  fallbackAction,
  allowWizardsModal = false,
}: AddMoreContentModalProps & { brandPlan: BrandPlan }) => {
  let initialStep: AddMoreContentModalStep = 'SelectSchedule'
  if (date) {
    initialStep = 'AddMoreContent'
  }
  if (allowWizardsModal) {
    initialStep = 'CreateSelect'
  }
  const [step, setStep] = useState<AddMoreContentModalStep>(initialStep)

  const [targetSchedule, setTargetSchedule] = useState<BrandPlanSchedule>()
  const [topic, setTopic] = useState(initialTopic || '')
  const [schedule, setSchedule] = useState<Schedule[]>([])
  const [additionals, setAdditionals] = useState(INITIAL_COUNT_STATE)
  const [mediaFiles, setMediaFiles] = useState<MinimalMediaFile[]>(initialMediaFiles || [])
  const { openModal, closeModal, closeAllModals } = useModals()
  const { mutate: addMoreContent, isLoading } = useAddToBrandPlanSchedule()
  const schedulesQuery = useBrandPlanSchedules({
    brandPlanId: brandPlan.id,
    start_date: dayjs().format('YYYY-MM-DD'),
    order_direction: 'asc',
  })
  const schedules = collectResults(schedulesQuery, (data) => data.schedules)
  const sortedSchedules = schedules.sort((a, b) => dayjs(a.start_date).diff(dayjs(b.start_date)))

  const scheduleContents = useMemo(() => {
    if (!targetSchedule) return []

    return generateSchedule(additionals, topic, mediaFiles, targetSchedule, date)
  }, [additionals, topic, mediaFiles, targetSchedule, date])

  const confirmSchedule = () => {
    if (!brandPlan || !targetSchedule) {
      showToastMessage(
        'Unable to schedule your content. Try again. Please contact support if the problem persists.',
        'error',
      )
      return
    }

    addMoreContent(
      { schedule: scheduleContents, brandPlanId: brandPlan.id, brandPlanScheduleId: targetSchedule.id },
      {
        onSuccess: () => {
          onSuccess?.(schedule.length)
          closeModal()
        },
      },
    )
  }

  const openConfirmationModal = () => {
    const totalAdditionals = sum(Object.values(additionals))

    if (totalAdditionals > 0 || topic.length > 0) {
      openModal(
        ConfirmationModal,
        {
          onConfirm: closeAllModals,
          onCancel: closeModal,
        },
        {
          newStack: true,
        },
      )
      return
    }

    closeModal()
  }

  useEffect(() => {
    if (!date || !sortedSchedules.length) return

    const dateSchedule = sortedSchedules.find(
      (schedule) =>
        dayjs(schedule.start_date).isSameOrBefore(date, 'day') && dayjs(date).isSameOrBefore(schedule.end_date, 'day'),
    )
    if (dateSchedule) {
      setTargetSchedule(dateSchedule)
    } else {
      closeModal()
      fallbackAction?.()
    }
  }, [date, sortedSchedules, fallbackAction, closeModal])

  if (!brandPlan) return null

  // can't show add more content until a schedule is there
  if (!targetSchedule && step === 'AddMoreContent') {
    return null
  }

  return (
    <Modal.Root onPressOutside={openConfirmationModal}>
      {step === 'CreateSelect' && (
        <CreateSelect
          onNext={() => setStep('SelectSchedule')}
          onWizardsBack={() => {
            setStep('CreateSelect')
            closeModal()
          }}
        />
      )}
      {step === 'SelectSchedule' && (
        <SelectBrandPlanSchedule
          onConfirm={(selectedSchedule) => {
            setTargetSchedule(selectedSchedule)
            setStep('AddMoreContent')
          }}
          onBack={allowWizardsModal ? () => setStep('CreateSelect') : undefined}
          schedules={sortedSchedules}
        />
      )}
      {step === 'AddMoreContent' && (
        <AddMoreContent
          targetSchedule={targetSchedule}
          additionals={additionals}
          setAdditionals={setAdditionals}
          onNext={() => setStep('TopicAndMedia')}
          onBack={date ? undefined : () => setStep('SelectSchedule')}
        />
      )}
      {step === 'TopicAndMedia' && (
        <TopicAndMedia
          topic={topic}
          setTopic={setTopic}
          mediaFiles={mediaFiles}
          setMediaFiles={setMediaFiles}
          onNext={() => setStep('ReviewPostTimes')}
          onBack={() => {
            setStep('AddMoreContent')
          }}
        />
      )}
      {step === 'ReviewPostTimes' && targetSchedule && (
        <ReviewPostTimes
          onBack={() => {
            setSchedule([])
            setStep('TopicAndMedia')
          }}
          onConfirm={confirmSchedule}
          schedule={scheduleContents}
          setSchedule={setSchedule}
          targetSchedule={targetSchedule}
          isLoading={isLoading}
          selectedBlogPlatform={brandPlan?.selected_blog_platform}
          disableDatePicker={!!date}
        />
      )}
    </Modal.Root>
  )
}

export { AddMoreContentModalWrapper as AddMoreContentModal }
